import { useState, useEffect } from 'react';
import { healthAPI } from '../../api/client';
import type { HealthStatus } from '../../types';

const POLL_INTERVAL = 30000;

function getStatusColor(status: string): string {
  const s = status.toLowerCase();
  if (['ok', 'connected', 'running', 'healthy', 'closed'].includes(s)) return 'text-emerald-500';
  if (['warning', 'reconnecting', 'degraded'].includes(s)) return 'text-amber-500';
  if (['not_configured', 'unknown'].includes(s)) return 'text-gray-400';
  return 'text-red-500';
}

function getStatusIcon(status: string): string {
  const s = status.toLowerCase();
  if (['ok', 'connected', 'running', 'healthy', 'closed'].includes(s)) return 'check_circle';
  if (['warning', 'reconnecting', 'degraded'].includes(s)) return 'warning';
  if (['not_configured', 'unknown'].includes(s)) return 'help_outline';
  return 'cancel';
}

function getStatusBg(status: string): string {
  const s = status.toLowerCase();
  if (['ok', 'connected', 'running', 'healthy', 'closed'].includes(s)) return 'bg-emerald-50 border-emerald-100';
  if (['warning', 'reconnecting', 'degraded'].includes(s)) return 'bg-amber-50 border-amber-100';
  if (['not_configured', 'unknown'].includes(s)) return 'bg-gray-50 border-gray-100';
  return 'bg-red-50 border-red-100';
}

function formatUptime(startTime: string): string {
  const start = new Date(startTime);
  const now = new Date();
  const diffMs = now.getTime() - start.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
  if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`;
  if (diffMins > 0) return `${diffMins}m ${diffSeconds % 60}s`;
  return `${diffSeconds}s`;
}

interface StatusCardProps {
  name: string;
  icon: string;
  status: string;
  detail?: string;
}

function StatusCard({ name, icon, status, detail }: StatusCardProps) {
  const color = getStatusColor(status);
  const statusIcon = getStatusIcon(status);
  const bg = getStatusBg(status);

  return (
    <div className={`flex items-center gap-3 p-4 rounded-xl border ${bg} transition-all`}>
      <span className="material-icons-round text-ink-secondary text-2xl">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink" data-ph-no-mask>{name}</p>
        {detail && <p className="text-xs text-ink-secondary mt-0.5" data-ph-no-mask>{detail}</p>}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className={`material-icons-round text-xl ${color}`}>{statusIcon}</span>
        <span className={`text-sm font-medium capitalize ${color}`} data-ph-no-mask>{status}</span>
      </div>
    </div>
  );
}

/** Embeddable health panel — no page wrapper, no h1. Pass active=false to stop polling. */
export function HealthPanel({ active = true }: { active?: boolean }) {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  async function fetchHealth() {
    try {
      const data = await healthAPI.detailed();
      setHealth(data);
      setLastUpdated(new Date());
      setSecondsAgo(0);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch health:', err);
      setError('Failed to load health status');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!active) return;
    void fetchHealth();
    const interval = setInterval(() => {
      void fetchHealth();
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [active]);

  useEffect(() => {
    if (!lastUpdated) return;
    const interval = setInterval(() => {
      const secs = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
      setSecondsAgo(secs);
    }, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          {lastUpdated && (
            <p className="text-xs text-ink-tertiary" data-ph-no-mask>Last updated {secondsAgo}s ago</p>
          )}
        </div>
        <button
          onClick={() => void fetchHealth()}
          className="p-1.5 rounded-full hover:bg-canvas-secondary transition-colors"
          aria-label="Refresh health status"
        >
          <span className="material-icons-round text-ink-secondary text-lg">refresh</span>
        </button>
      </div>

      {health && (
        <div className={`flex items-center gap-3 p-4 rounded-2xl border mb-4 ${
          health.status === 'healthy'
            ? 'bg-emerald-50 border-emerald-100'
            : 'bg-red-50 border-red-100'
        }`}>
          <span className={`material-icons-round text-3xl ${
            health.status === 'healthy' ? 'text-emerald-500' : 'text-red-500'
          }`}>
            {health.status === 'healthy' ? 'check_circle' : 'warning'}
          </span>
          <div>
            <p className={`text-lg font-bold ${
              health.status === 'healthy' ? 'text-emerald-700' : 'text-red-700'
            }`}>
              <span data-ph-no-mask>{health.status === 'healthy' ? 'All Systems Operational' : 'System Issues Detected'}</span>
            </p>
            {health.start_time && (
              <p className="text-sm text-ink-secondary">Uptime: {formatUptime(health.start_time)}</p>
            )}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-edge border-t-ink-secondary rounded-full animate-spin mb-4" />
          <p className="text-ink-secondary" data-ph-no-mask>Loading health status...</p>
        </div>
      )}

      {error && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <span className="material-icons-round text-red-400 text-5xl mb-3">error_outline</span>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => void fetchHealth()}
            className="px-4 py-2 bg-ink text-white rounded-lg hover:opacity-90 transition-colors"
            data-ph-no-mask
          >
            Retry
          </button>
        </div>
      )}

      {health && !isLoading && (
        <div className="space-y-3">
          <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider mb-3" data-ph-no-mask>Subsystems</h2>

          <StatusCard
            name="WhatsApp"
            icon="chat"
            status={health.whatsapp.status}
            detail="WhatsApp connection via WuzAPI"
          />

          <StatusCard
            name="Scheduler"
            icon="schedule"
            status={health.scheduler.status}
            detail={
              health.scheduler.job_count !== undefined
                ? `${health.scheduler.job_count} job${health.scheduler.job_count !== 1 ? 's' : ''} registered`
                : 'APScheduler'
            }
          />

          <StatusCard
            name="Outbound Breaker"
            icon="shield"
            status={health.outbound?.circuit_breaker.state || 'unknown'}
            detail={`Failures: ${health.outbound?.circuit_breaker.consecutive_failures ?? 0}/${health.outbound?.circuit_breaker.threshold ?? 0}`}
          />

          <StatusCard
            name="Database"
            icon="storage"
            status={health.database.status}
            detail="SQLite with WAL mode"
          />

          {health.disk && (
            <StatusCard
              name="Disk"
              icon="hard_drive"
              status={health.disk.status}
              detail={`${health.disk.used_pct.toFixed(1)}% used`}
            />
          )}

          {health.memory && (
            <StatusCard
              name="Memory"
              icon="memory"
              status={health.memory.status}
              detail={`${health.memory.used_pct.toFixed(1)}% used`}
            />
          )}
        </div>
      )}

      {health && !isLoading && (
        <div className="mt-6 space-y-4">
          <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>Resource Usage</h2>

          {health.disk && (
            <div className="p-4 rounded-xl bg-white border border-edge space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-ink" data-ph-no-mask>Disk</span>
                <span className="text-sm text-ink-secondary">{health.disk.used_pct.toFixed(1)}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-canvas-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    health.disk.used_pct >= 90 ? 'bg-red-500' :
                    health.disk.used_pct >= 75 ? 'bg-amber-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(health.disk.used_pct, 100)}%` }}
                />
              </div>
            </div>
          )}

          {health.memory && (
            <div className="p-4 rounded-xl bg-white border border-edge space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-ink" data-ph-no-mask>Memory</span>
                <span className="text-sm text-ink-secondary">{health.memory.used_pct.toFixed(1)}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-canvas-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    health.memory.used_pct >= 95 ? 'bg-red-500' :
                    health.memory.used_pct >= 85 ? 'bg-amber-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.min(health.memory.used_pct, 100)}%` }}
                />
              </div>
            </div>
          )}

          <div className="p-4 rounded-xl bg-white border border-edge space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-ink" data-ph-no-mask>Outbound usage (hour/day)</span>
              <span className="text-sm text-ink-secondary">
                {health.outbound?.rate_limiter.usage.global_hourly ?? 0}/{health.outbound?.rate_limiter.limits.global_hourly_max ?? 0}
                {' · '}
                {health.outbound?.rate_limiter.usage.global_daily ?? 0}/{health.outbound?.rate_limiter.limits.global_daily_max ?? 0}
              </span>
            </div>
            <div className="text-xs text-ink-secondary">
              Per-contact daily cap: {health.outbound?.rate_limiter.limits.per_contact_daily_max ?? 0}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function HealthScreen() {
  return (
    <div className="h-full overflow-y-auto glass-scroll">
      <div className="max-w-md sm:max-w-lg md:max-w-2xl mx-auto px-4 pt-6 pb-24">
        <h1 className="text-2xl font-bold text-ink mb-6" data-ph-no-mask>System Health</h1>
        <HealthPanel active />
      </div>
    </div>
  );
}

export default HealthScreen;
